/**
 * ⛔⛔ THE ONE REPORT MODEL — Card P156.
 *
 * Every export (print, PDF, Word, Excel) is rendered from THIS, and this is
 * built from the SAME props the Overview screen renders. There is no second
 * query and no second interpretation: if the screen says $3.2M, the
 * spreadsheet says 3200000, because both read this object.
 *
 * ⚠⚠ A SECOND QUERY IS HOW AN EXPORT COMES TO DISAGREE WITH ITS SCREEN, and
 * a board pack that disagrees with the dashboard it was printed from is worse
 * than no board pack — nobody can tell which one is wrong.
 *
 * ── ⛔⛔ SUMMARY LEVEL ONLY. NO PATIENT NAMES. EVER. ────────────────────────
 * These files are emailed, printed and left on desks. Row-level detail stays
 * in the drill-down CSV behind `analytics:drilldown:export`, which is a
 * separate permission for exactly that reason. This model carries counts,
 * money and stage/rep labels — never a contact.
 *
 * Rep names ARE staff names and are the point of a team table; they are not
 * patient data. The distinction is deliberate and is pinned by a red arm.
 */

/** Money is carried in CENTS as an integer, with its own type tag. */
export const money = (cents) => ({ kind: 'money', cents: Math.round(Number(cents) || 0) });
export const count = (n) => ({ kind: 'count', n: Number(n) || 0 });
export const percent = (p) => ({ kind: 'percent', p: Number(p) || 0 });
/** ⚠ An absent figure is its own kind — never 0, in any format. */
export const absent = (reason) => ({ kind: 'absent', reason: reason || 'not tracked yet' });

/*
 * ⛔⛔ THE UNIT TAG NAMES THE SCALE — P161 (#746), and this file missed it.
 *
 * Both builders used to send `unit: "currency"` while meaning DIFFERENT
 * scales: routes/_finance_summary.js cents, routes/_ceo_dashboard.js dollars.
 * P161 settled it by making the tag say which — `"cents"` and `"dollars"` —
 * which is the better fix, because the ambiguity is gone rather than
 * normalised away.
 *
 * ⚠⚠ BUT THIS MODULE STILL ONLY RECOGNISED `"currency"`, so after #746
 * merged, every money figure in the PDF, Word and Excel exports fell through
 * to the count branch: $3,200,000.00 rendered as "3,200,000". Measured, not
 * inferred. The screen was updated in the same PR; the export path was not.
 *
 * ⚠ `"currency"` is still accepted — a server mid-deploy still sends it, and
 * the old meaning on THIS side was always dollars.
 */
const DOLLAR_UNITS = Object.freeze(['dollars', 'currency']);

const cellOf = (m) => {
  if (!m || m.tracked === false || m.value === null || m.value === undefined) {
    return absent(m && m.reason ? m.reason : 'not tracked yet');
  }
  if (m.unit === 'cents') return money(Number(m.value));
  if (DOLLAR_UNITS.includes(m.unit)) return money(Number(m.value) * 100);
  if (m.unit === 'percent') return percent(m.value);
  return count(m.value);
};

/**
 * Build the report.
 *
 * @param {object} args  the SAME values the Overview renders
 *   data, finance, attention, reps, stages, period, asOf, canFinance
 * @returns {{title, period, asOf, sections: Array}}
 */
export function buildReportModel({
  data = null, finance = null, attention = null, reps = [], stages = null,
  period = 'Month', asOf = null, canFinance = false, charts = {},
} = {}) {
  const sections = [];
  const m = (key, legacy, unit) => {
    if (data && data.metrics && data.metrics[key]) return cellOf({ ...data.metrics[key] });
    /*
     * ⛔⛔ GUARD BEFORE CALLING, NOT AFTER. The legacy accessors reach into
     * `data` (`d.revenue?.bookedWon`), and this called them with whatever
     * `data` was and checked for null afterwards — so a report built with
     * finance present and the ceo payload ABSENT threw on the first figure.
     *
     * ⚠ That is a real production state, not a test artefact: the two
     * channels load independently, and `reports:ceo:run` failing while
     * `reports:finance:summary` succeeds is exactly the case the three-state
     * cards exist for. The export must degrade to "not tracked", not crash.
     */
    if (!data) return absent();
    let v;
    try {
      v = typeof legacy === 'function' ? legacy(data) : undefined;
    } catch (e) {
      return absent();
    }
    if (v === undefined || v === null) return absent();
    if (unit === 'cents') return money(Number(v));
    if (DOLLAR_UNITS.includes(unit)) return money(Number(v) * 100);
    return unit === 'percent' ? percent(v) : count(v);
  };

  /*
   * ⭐⭐ THE EXPORTS CARRY THE SAME LABELS AS THE SCREEN — Card P158.
   *
   * ⚠⚠ The finance window and the point-in-time snapshot are DIFFERENT
   * CLOCKS, and a printed page is where that confusion does the most damage:
   * nobody can hover a PDF to find out. So the range label travels with the
   * collected figure, and the receivables section says "as of" in its title.
   */
  const fin = canFinance && finance && finance.tracked ? finance : null;
  const periodOf = (key) => (fin && fin.period ? fin.period[key] : null);
  /* ⚠ No range block (pre-#744 server) ⇒ the figure IS all-time, and the
     label must say so rather than borrowing the period pill's wording. */
  const rangeLabel = fin
    ? (fin.range ? (fin.range.label || (fin.range.from ? 'Selected period' : 'All time')) : 'All time')
    : null;
  const pit = fin && fin.pointInTime ? fin.pointInTime : null;
  const collectedM = periodOf('collected');

  // ── 1 · Headline ────────────────────────────────────────────────────────
  sections.push({
    key: 'headline', title: rangeLabel ? `Headline — ${rangeLabel}` : 'Headline',
    kind: 'figures',
    figures: [
      { label: rangeLabel ? `Collected (${rangeLabel})` : 'Collected',
        /*
         * ⛔⛔ FINANCE CURRENCY IS IN CENTS; THE CEO ENVELOPE'S IS IN DOLLARS.
         *
         * Both carry `unit: "currency"` and they are NOT the same scale:
         * routes/_finance_summary.js passes `cur.succeededCents`, while
         * routes/_ceo_dashboard.js passes SUM(crm_opportunities.value), which
         * is a NUMERIC in dollars. Reading one with the other's assumption is
         * a 100× error on a board report, in whichever direction.
         *
         * ⚠ Handled here rather than papered over: the conversion is explicit
         * and named at each call site. The real fix is one scale across both
         * builders, which is server-side and not this card's to make — raised
         * in the PR.
         *
         * ⚠⚠ AND IT FALLS BACK. A server that has not deployed #744 sends no
         * `period` block at all; the all-time `collected.cents` is then the
         * only figure there is, and showing nothing would be worse than
         * showing it with an honest label.
         */
        value: collectedM && collectedM.tracked !== false && collectedM.value !== null
          ? money(collectedM.value)                       // already cents
          : (fin && fin.collected ? money(fin.collected.cents)
             : absent(canFinance ? 'not tracked yet' : 'requires finance access')),
        note: collectedM && collectedM.provisional
          ? 'Provisional — partial refunds still syncing.' : null },
      { label: 'Booked (won deals)', value: m('bookedWonValue', (d) => d.revenue?.bookedWonValue ?? d.revenue?.bookedWon, 'dollars') },
      { label: 'Active pipeline', value: m('openValue', (d) => d.pipeline?.openValue, 'dollars') },
      { label: 'New leads', value: m('leadsThisMonth', (d) => d.leadGen?.thisMonth, 'count') },
      { label: 'Lead → Deposit', value: m('leadToDeposit', (d) => d.conversion?.leadToDeposit, 'percent') },
    ],
  });

  // ── 2 · Revenue trend ───────────────────────────────────────────────────
  const monthly = canFinance && finance && Array.isArray(finance.monthly) ? finance.monthly : null;
  sections.push({
    key: 'trend', title: 'Collected revenue — 12 months',
    kind: 'table', chart: charts.trend || null,
    columns: ['Month', 'Collected', 'Forecast'],
    rows: monthly ? monthly.map((r) => [r.month, money(r.collectedCents), money(r.forecastCents || 0)]) : [],
    note: monthly ? null : (canFinance ? 'A month-by-month series is not in the finance summary yet.' : 'Requires finance access.'),
  });

  // ── 3 · Invoice status ──────────────────────────────────────────────────
  const buckets = canFinance && finance && finance.invoices ? finance.invoices.byBucket : null;
  sections.push({
    key: 'invoices', title: 'Invoice status', kind: 'table', chart: charts.invoices || null,
    columns: ['Status', 'Invoices'],
    rows: buckets ? Object.entries(buckets).map(([k, v]) => [k.replace(/_/g, ' '), count(v.n)]) : [],
    note: buckets ? null : 'Requires finance access.',
  });

  // ── 4 · Exceptions ──────────────────────────────────────────────────────
  sections.push({
    key: 'exceptions', title: 'Exceptions requiring attention', kind: 'table',
    columns: ['Item', 'Count', 'At risk'],
    rows: ((attention && attention.items) || []).slice(0, 10).map((it) => [
      String(it.title || ''), count(it.count),
      it.dollarsAtRisk === null || it.dollarsAtRisk === undefined
        ? absent('no $ value') : money(Number(it.dollarsAtRisk) * 100),
    ]),
    note: ((attention && attention.notTracked) || []).map((n) => `${n.label}: ${n.message}`).join(' ') || null,
  });

  // ── 5 · Team ────────────────────────────────────────────────────────────
  sections.push({
    key: 'team', title: 'Team — overdue follow-ups by rep', kind: 'table', chart: charts.team || null,
    columns: ['Rep', 'Overdue follow-ups', 'Won'],
    rows: (reps || []).map((r) => [
      String(r.label || r.rep || ''), count(r.overdue),
      r.wonDollars === null || r.wonDollars === undefined ? absent() : money(Number(r.wonDollars) * 100),
    ]),
  });

  // ── 6 · Receivables — finance only ──────────────────────────────────────
  /*
   * ⛔⛔ OMITTED ENTIRELY WITHOUT finance:read — not blanked, not zeroed, not
   * present-with-a-notice. A section header reading "Receivables" over empty
   * cells still tells a reader this organisation has receivables and that
   * someone chose not to show them. Absent is absent.
   */
  /*
   * ⚠ `tracked: true` is not a promise that every sub-object is present. A
   * summary carrying period figures but no receivables block is a real
   * shape — and reaching into `finance.overdue.outstandingCents` on it
   * throws, taking the WHOLE export down over a section that is merely
   * absent. The section is emitted only when its own data is there.
   */
  const hasReceivables = canFinance && finance && finance.tracked
    && finance.overdue && finance.deposits;
  if (hasReceivables) {
    /*
     * ⛔⛔ THE BANDS COME FROM THE SERVER, KEYS AND LABELS BOTH (card P256).
     *
     * This read `aging['1-30']` while routes/_finance_summary.js has always
     * emitted `d1_30`, so every one of the four bands rendered $0 under an
     * "Overdue total" showing the real figure — a receivables table that said
     * the money was nowhere. src/ cannot import routes/, so the fix is not a
     * second copy of the vocabulary here: the server sends `agingRows` with the
     * label and order attached, and this maps over them without naming a band.
     *
     * ⚠ The fallback is the OLD object, not an empty one, so a summary from a
     * server that predates this card still renders its bands rather than
     * silently showing zeros — the failure mode being fixed.
     */
    const rows = Array.isArray(finance.overdue.agingRows) && finance.overdue.agingRows.length
      ? finance.overdue.agingRows.map((r) => [r.label, money(r.outstandingCents || 0), count(r.n || 0)])
      : Object.entries((finance.overdue && finance.overdue.aging) || {})
          .map(([k, v]) => [k, money((v || {}).outstandingCents || 0), count((v || {}).n || 0)]);

    sections.push({
      key: 'receivables',
      /*
       * ⚠ THE TITLE IS THE EXCEL SHEET NAME. Carrying "aged by due date" in it
       * pushed the name to 30 characters, one under Excel's 31-char ceiling,
       * and broke every lookup keyed on sheetName('Receivables'). The basis
       * belongs in the note and the column header, which are not identifiers.
       */
      title: pit ? 'Receivables — as of today' : 'Receivables',
      note: [
        'Aged by due date. Overdue means past its due date and not fully paid — the remaining balance, not the invoice total.',
        pit ? pit.note : null,
      ].filter(Boolean).join(' '),
      kind: 'table',
      columns: ['Band (aged by due date)', 'Outstanding', 'Invoices'],
      rows: [
        ...rows,
        ['Overdue total', money(finance.overdue.outstandingCents), count(finance.overdue.count)],
        ['Deposits received', money(finance.deposits.receivedCents), count(finance.deposits.count)],
      ],
    });
  }

  // ── 7 · Top stages ──────────────────────────────────────────────────────
  sections.push({
    key: 'stages', title: 'Where the open pipeline sits', kind: 'table',
    columns: ['Stage', 'Deals', 'Value'],
    rows: (stages || []).slice(0, 12).map((g) => [
      g.key === null || g.key === undefined ? '(no stage set)' : String(g.key),
      count(g.n), money(g.totalCents || 0),
    ]),
  });

  return {
    title: 'ZignaGenix — CEO Report',
    period: String(period || 'Month'),
    /* ⚠ The SERVER's window, beside the pill label — they are not the same
       thing, and an export that showed only the pill would hide which
       window the finance figures actually used. */
    rangeLabel: rangeLabel || null,
    pointInTimeNote: pit ? pit.note : null,
    asOf: asOf || new Date().toISOString(),
    sections,
  };
}

/**
 * The filename stem. ⚠ Sanitised: a period label reaches this from the UI and
 * a slash or colon in a filename is a path, not a name.
 */
export function reportFilename(model, ext) {
  const day = String(model.asOf || '').slice(0, 10) || 'undated';
  const period = String(model.period || 'period').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `ZignaGenix_CEO_Report_${period}_${day}.${ext}`;
}

/** Plain text for any cell — used by print, PDF and Word. */
export function cellText(c) {
  if (c === null || c === undefined) return '';
  if (typeof c === 'string') return c;
  if (c.kind === 'absent') return `— ${c.reason}`;
  if (c.kind === 'money') return `$${(c.cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (c.kind === 'percent') return `${c.p}%`;
  if (c.kind === 'count') return c.n.toLocaleString('en-US');
  return String(c);
}
